import * as React from "react";

import { ChevronLeftIcon, ChevronRightIcon } from "@lucide/icons";

import { Button } from "simplydo/react/ui/button";
import { Icon } from "simplydo/react/ui/icon";
import {
  TableHeader,
  TableRow,
  TableHeaderCell,
} from "simplydo/react/ui/table";

import { isNil } from "../../lib/utils";
import type { BodyWidth } from "../CalendarBody/Body";
import HeaderRange from "./HeaderRange";
import HeaderWeeks from "./HeaderWeeks";

export interface HeaderProps {
  /** Header text content. */
  title: string;
  /** Called after click on next page button. */
  onNextPageBtnClick: () => void;
  /** Called after click on previous page button. */
  onPrevPageBtnClick: () => void;
  /** Whether to display previous page button as active or disabled. */
  hasPrevPage: boolean;
  /** Whether to display next page button as active or disabled. */
  hasNextPage: boolean;
  /** Whether to display weeks row or not. */
  displayWeeks: boolean;
  /** Header width. */
  width: BodyWidth;
  /** Text content to display in dates-range row. */
  rangeRowContent?: string;
  /** Called after click on calendar header. */
  onHeaderClick?: () => void;
  /** Moment date localization */
  localization?: string;
  className?: string;
}

function Header(props: HeaderProps) {
  const {
    rangeRowContent,
    displayWeeks,
    onNextPageBtnClick,
    onPrevPageBtnClick,
    hasPrevPage,
    hasNextPage,
    onHeaderClick,
    width,
    title,
    localization,
    className,
  } = props;

  const cellStyle = {
    border: "none",
    borderBottom: displayWeeks ? "none" : "1px solid rgba(34,36,38,.1)",
  };
  const headerTitleStyle = { cursor: onHeaderClick ? "pointer" : "default" };

  return (
    <TableHeader className={className}>
      {!isNil(rangeRowContent) && <HeaderRange content={rangeRowContent} />}
      <TableRow>
        <TableHeaderCell style={cellStyle} colSpan={1}>
          <Button
            size="small"
            variant="outline"
            disabled={!hasPrevPage}
            onClick={hasPrevPage ? onPrevPageBtnClick : undefined}
          >
            <Icon icon={ChevronLeftIcon} />
          </Button>
        </TableHeaderCell>

        <TableHeaderCell
          onClick={onHeaderClick ? onHeaderClick : undefined}
          style={cellStyle}
          colSpan={(width - 2).toString() as any}
        >
          <span style={headerTitleStyle}>{title}</span>
        </TableHeaderCell>

        <TableHeaderCell style={cellStyle} colSpan={1}>
          <Button
            size="small"
            variant="outline"
            disabled={!hasNextPage}
            onClick={hasNextPage ? onNextPageBtnClick : undefined}
          >
            <Icon icon={ChevronRightIcon} />
          </Button>
        </TableHeaderCell>
      </TableRow>
      {displayWeeks && <HeaderWeeks localization={localization} />}
    </TableHeader>
  );
}

export default Header;
